import { FolderOutlined } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';

import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import { useFileFolderSelection } from '../../components/Providers/FileFolderSelectionProvider';
import { CustomFile } from '../../Types/file';
import { formatDate } from '../../util/dateUtils';
import { extractUniqueFolders, getFolderMetadata } from '../../util/FolderPathUtil';

interface FolderListProps {
  files: CustomFile[];
  onSearch: (query: string) => void;
  searchQuery: string;
}

const ROW_HEIGHT = 56;

const FolderList: React.FC<FolderListProps> = ({ files, onSearch, searchQuery }) => {
  const { selectedFolder, handleFolderSelect } = useFileFolderSelection();
  const folders = useMemo(() => {
    return extractUniqueFolders(files);
  }, [files]);

  const handleClick = useCallback(
    (folder: string) => {
      const isSelected = selectedFolder === folder;
      handleFolderSelect(isSelected ? null : folder, files);
    },
    [files, selectedFolder, handleFolderSelect]
  );

  return (
    <>
      <Box sx={{ py: 1 }}>
        <DebouncedSearchInput labelText="Search" value={searchQuery} valueUpdated={onSearch} />
      </Box>
      <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
        <Table size="small" sx={{ border: '1px solid #ccc' }}>
          <TableHead>
            <TableRow sx={{ height: ROW_HEIGHT }}>
              {['Folder Name', 'Last Updated', 'Modified By'].map((title) => (
                <TableCell key={title}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {title}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {folders.map((folder) => {
              const metadata = getFolderMetadata(folder, files);

              return (
                <TableRow
                  key={folder}
                  hover
                  selected={selectedFolder === folder}
                  onClick={() => handleClick(folder)}
                  sx={{
                    cursor: 'pointer',
                    height: ROW_HEIGHT,
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={selectedFolder === folder}
                        onChange={() => handleClick(folder)}
                        sx={{ padding: 0, marginRight: 1 }}
                        inputProps={{ 'aria-label': `Select folder ${folder}` }}
                      />
                      <FolderOutlined sx={{ marginRight: 1 }} />
                      <Typography>{folder}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold">
                      {formatDate(metadata?.lastModified ?? '')}
                    </Typography>
                  </TableCell>
                  <TableCell>{metadata?.userId ?? '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};

export default FolderList;
